import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Button,
  Grid,
  useTheme,
  useMediaQuery,
  Alert
} from '@mui/material';
import { FaBuilding, FaArrowRight, FaChartLine, FaShieldAlt } from 'react-icons/fa';
import PurchaseIncomeAssets from './PurchaseIncomeAssets';
import PurchaseGrowthAssets from './PurchaseGrowthAssets';
import { useDispatch, useSelector } from 'react-redux';
import { getLandingData } from '../../../slices/fundManagementInvestment/fundmanagementInvestmentSlice';
import { useDrawer } from 'components/app/DrawerContext';
import { useAppSelector } from 'app/hooks';

interface AssetClass {
  id: string;
  name: string;
  type: 'Income' | 'Growth';
  description: string;
  features: string[];
  riskLevel: string;
  icon: React.ReactNode;
  color: string;
}

const assetClasses: AssetClass[] = [
  {
    id: '1',
    name: 'Income Assets',
    type: 'Income',
    description:
      'Purchase income-generating assets for the fund portfolio to provide regular distributions',
    features: [
      'Rental properties with tenant income',
      'Dividend-paying stocks and bonds',
      'Government and corporate bonds',
      'Regular cash flow for distributions'
    ],
    riskLevel: 'Conservative to Moderate',
    icon: <FaBuilding size={32} />,
    color: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
  },
  {
    id: '2',
    name: 'Growth Assets',
    type: 'Growth',
    description:
      'Acquire growth-focused assets for long-term capital appreciation and fund expansion',
    features: [
      'Development sites and growth properties',
      'Technology and growth stocks',
      'Private equity opportunities',
      'Capital appreciation focus'
    ],
    riskLevel: 'Moderate to High',
    icon: <FaChartLine size={32} />,
    color: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'
  }
];

const AssetsPurchase: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const { fundManagementAdminRight } = useDrawer();

   const selectedProjectId = useAppSelector((state) => state.homeTilesServices.selectedProjectId);

  const landingData = useSelector((state: any) => state.fundManagementInvestment.landingData) || [];
  const [isAdmin, setIsAdmin] = useState(fundManagementAdminRight);
  const [selectedAssetClass, setSelectedAssetClass] = useState<AssetClass | null>(null);

  useEffect(() => {
   

    if (selectedProjectId) {
      console.log('Loading landing data for unit purchase...');
      dispatch(
        getLandingData({
          groupId: selectedProjectId,
          serviceId: 'fundManagementInvestment',
          isAdmin: isAdmin
        })
      );
    }
  }, [dispatch, isAdmin]);

  const handleAssetClassSelect = (assetClass: AssetClass) => {
    setSelectedAssetClass(assetClass);
  };

  const handleBackToSelection = () => {
    setSelectedAssetClass(null);
  };

  const getRiskColor = (riskLevel: string) => {
    if (riskLevel.includes('Conservative')) return '#4caf50';
    if (riskLevel.includes('Moderate')) return '#ff9800';
    return '#f44336';
  };

  const incomeUnitsData = landingData.find((item: any) => item.unitType === 'incomeUnits');
  const growthUnitsData = landingData.find((item: any) => item.unitType === 'growthUnits');

  const incomeUnitsCashRemaining = incomeUnitsData ? Number(incomeUnitsData.cashRemaining) : 0;
  const growthUnitsCashRemaining = growthUnitsData ? Number(growthUnitsData.cashRemaining) : 0;

  if (selectedAssetClass) {
    if (selectedAssetClass.type === 'Income') {
      return (
        <PurchaseIncomeAssets
          onBack={handleBackToSelection}
          cashRemaining={incomeUnitsCashRemaining}
        />
      );
    } else {
      return (
        <PurchaseGrowthAssets
          onBack={handleBackToSelection}
          cashRemaining={growthUnitsCashRemaining}
        />
      );
    }
  }

  return (
    <Container maxWidth="xl" sx={{ padding: { xs: 1, sm: 2, md: 3 }, minHeight: '100vh' }}>
      <Alert
        severity="info"
        sx={{
          marginBottom: 3,
          backgroundColor: '#e8f4fd',
          border: '1px solid #1976d2',
          borderRadius: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FaShieldAlt color="#1976d2" />
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            Fund Manager - Asset Acquisition Portal
          </Typography>
        </Box>
      </Alert>

      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: { xs: 2, md: 3 } }}>
        <FaBuilding size={isMobile ? 20 : 24} color="#1976d2" style={{ marginRight: 12 }} />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 500, color: '#333' }}>
            Fund Asset Acquisition
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Purchase strategic assets for the investment fund portfolio
          </Typography>
        </Box>
      </Box>

      <Typography variant="body1" sx={{ fontWeight: 500, marginBottom: 2.5, color: '#333' }}>
        Select Asset Category for Fund Portfolio
      </Typography>

      <Grid container spacing={4}>
        {assetClasses.map((assetClass) => (
          <Grid item xs={12} md={6} key={assetClass.id}>
            <Card
              sx={{
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                borderRadius: 3,
                border: '2px solid #e0e0e0',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                  border: '2px solid #1976d2'
                }
              }}
              onClick={() => handleAssetClassSelect(assetClass)}
            >
              <Box sx={{ background: assetClass.color, padding: 3, color: 'white' }}>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 500, marginBottom: 1 }}>
                      {assetClass.name}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Fund Portfolio Management
                    </Typography>
                  </Box>
                  <Box>{assetClass.icon}</Box>
                </Box>
              </Box>

              <CardContent sx={{ padding: 3 }}>
                <Typography
                  variant="body1"
                  sx={{ marginBottom: 2, color: '#666', lineHeight: 1.6 }}
                >
                  {assetClass.description}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, marginBottom: 2, color: '#333' }}
                >
                  Portfolio Benefits:
                </Typography>
                <Box sx={{ marginBottom: 3 }}>
                  {assetClass.features.map((feature, index) => (
                    <Typography
                      key={index}
                      variant="body2"
                      sx={{ color: '#666', marginBottom: 0.5 }}
                    >
                      • {feature}
                    </Typography>
                  ))}
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 2
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: getRiskColor(assetClass.riskLevel),
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '16px',
                      fontSize: '0.65rem',
                      fontWeight: 500
                    }}
                  >
                    Risk Profile: {assetClass.riskLevel}
                  </Box>
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  endIcon={<FaArrowRight />}
                  sx={{
                    backgroundColor: '#1976d2',
                    '&:hover': { backgroundColor: '#1565c0' },
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                >
                  Purchase {assetClass.name}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default AssetsPurchase;
